import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Goal as GoalModel,
  IGetOrCreateGoalSegmentResult,
  IPredicate,
  TComparison,
} from '@feathr/blackbox';
import type { IUniversalSelectOption } from '@feathr/components';
import { FormElement, SegmentedControl, UniversalCreatableSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import { buildURLVisitPredicates } from '../Goal.predicates';
import GoalDevConsole from '../GoalDevConsole';

import * as styles from './URLVisitGoal.css';

interface IProps {
  goal: GoalModel;
}

function URLVisitGoal({ goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Breadcrumbs, Segments } = useStore();
  const [matchMode, setMatchMode] = useState<string>('eq');
  const [selectedUrl, setSelectedUrl] = useState<IUniversalSelectOption | undefined>(undefined);
  const [currentPredicates, setCurrentPredicates] = useState<IPredicate[]>([]);
  const [segmentResult, setSegmentResult] = useState<IGetOrCreateGoalSegmentResult | undefined>(
    undefined,
  );

  const {
    spacing: { '2': gap },
  } = useMantineTheme();

  useEffect(() => {
    async function updateSegment(): Promise<void> {
      if (!selectedUrl?.id) {
        return;
      }

      const predicates = buildURLVisitPredicates(selectedUrl.id, matchMode as TComparison);
      const segmentName = `URL Visit Goal - ${goal.get('name') || 'Unnamed'}`;

      setCurrentPredicates(predicates);
      const result = await Segments.getOrCreateGoalSegment(predicates, segmentName);
      setSegmentResult(result);
      goal.set({ segment: result.segment.id });
    }

    if (selectedUrl?.id) {
      updateSegment();
    }
  }, [selectedUrl, matchMode, Segments, goal]);

  const data = [
    {
      label: t('Exact match'),
      value: 'eq',
    },
    {
      label: t('Starts with'),
      value: 'starts_with',
    },
  ];

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: readonly IUniversalSelectOption[]) => void): void => {
      const path = 'breadcrumbs/loc_url/values';
      Breadcrumbs.getSuggestions(path, inputValue)
        .then((suggestions) => {
          const options: IUniversalSelectOption[] = suggestions
            .filter((suggestion) => suggestion.value)
            .map(({ value }) => ({
              id: value,
              name: value,
            }));
          callback(options);
        })
        .catch(() => {
          callback([]);
        });
    },
    [Breadcrumbs],
  );

  function createOption(inputValue: string): IUniversalSelectOption {
    return {
      id: inputValue,
      name: inputValue,
    };
  }

  function handleChangeValue(value?: IUniversalSelectOption): void {
    setSelectedUrl(value);
  }

  function handleChangeMatchMode(value: string): void {
    setMatchMode(value);
  }

  return (
    <Flex direction={'column'} gap={gap}>
      <Flex align={'flex-end'} gap={gap}>
        <FormElement label={t('Target URL')}>
          <SegmentedControl data={data} onChange={handleChangeMatchMode} value={matchMode} />
        </FormElement>
        <UniversalCreatableSelect
          async={true}
          controlPrefix={getIconForAction('search')}
          createOption={createOption}
          defaultOptions={true}
          loadOptions={loadOptions}
          onChange={handleChangeValue}
          placeholder={t('Start typing to search URLs')}
          value={selectedUrl}
          wrapperClassName={styles.input}
        />
      </Flex>
      <GoalDevConsole predicates={currentPredicates} segmentResult={segmentResult} />
    </Flex>
  );
}

export default observer(URLVisitGoal);
